import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ShoppingCart, X, BellRing, ArrowLeft, Check, ChevronDown, MessageCircle, StickyNote } from 'lucide-react';
import { navigate } from '../App';
import { updateOrderStatus } from '../hooks/useStoreApi';

// Store API (orders + get-store-by-id) and the user-entities endpoint that
// tells us which stores this user administers. Both are duplicated elsewhere
// in the app; centralise later if a shared config emerges.
const STORE_API_URL = process.env.REACT_APP_STORE_API_URL || 'https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/prod';
const ENTITIES_API_URL = 'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default';
const POLL_INTERVAL_MS = 10000;

// An order counts as "unconfirmed" until the owner moves it past 'pending'.
// On app load we surface every unconfirmed order so nothing slips through.
const isUnconfirmed = (order) => (order?.status || 'pending') === 'pending';

// Israeli mobile detection + WhatsApp formatting (mirrors OrdersTab helpers).
const isMobilePhone = (phone) => {
    if (!phone) return false;
    const cleaned = phone.replace(/[\s\-()]/g, '');
    return /^(05\d{8}|(\+972|972)5\d{8})$/.test(cleaned);
};
const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/[\s\-()]/g, '');
    if (cleaned.startsWith('05')) return '972' + cleaned.substring(1);
    if (cleaned.startsWith('+972')) return cleaned.substring(1);
    return cleaned;
};
const fmtMoney = (v) => { const n = Number(v); return isNaN(n) ? v : (n % 1 === 0 ? n : n.toFixed(2)); };

/**
 * OrderDetails — the expanded view of a single order inside the modal.
 * Mirrors the fields shown in OrdersTab's expanded card, kept compact.
 */
const OrderDetails = ({ order }) => {
    const customerName = order.customer?.name || order.customer_name || 'לא צוין';
    const customerPhone = order.customer?.phone || order.customer_phone || '';
    const customerEmail = order.customer?.email || order.customer_email || '';
    const customerAddress = order.customer_address || order.customer?.address || '';
    const totalPrice = order.total || order.total_price || '0';
    const items = order.items || [];

    let deliveryRow = null;
    if (order.fulfillment_type === 'takeaway' || order.fulfillment_type === 'eat_in') {
        const fallback = order.fulfillment_type === 'takeaway' ? 'איסוף עצמי' : 'שירות במקום';
        deliveryRow = { label: 'אופן קבלה', value: order.delivery_location_name || fallback };
    } else if (order.fulfillment_type === 'delivery_area') {
        const areaName = order.delivery_location_name || order.delivery_area_name;
        if (areaName) deliveryRow = { label: 'משלוח ליישוב', value: areaName };
    } else if (order.delivery_point_name) {
        deliveryRow = { label: 'נקודת חלוקה', value: order.delivery_point_name };
    }

    // Pickup time slot: shown whenever the order carries an actual slot
    // selection. The label combines the slot date with its time range; either
    // part may be missing, so we build it from whatever is available.
    const hasTimeSlot = !!(order.time_slot_id || order.time_slot_date);
    let pickupText = '';
    if (hasTimeSlot) {
        const rawDate = order.time_slot_date || order.delivery_date;
        let datePart = '';
        if (rawDate) {
            const dt = new Date(rawDate);
            datePart = isNaN(dt.getTime()) ? rawDate : dt.toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric', year: 'numeric' });
        }
        pickupText = [datePart, order.delivery_time].filter(Boolean).join(' ');
    }

    return (
        <div className="px-3 pb-3 pt-1 bg-surface-muted/50 border-t border-line-subtle space-y-3 text-xs">
            {/* Customer */}
            <div className="bg-white rounded-lg p-3 border border-line space-y-1">
                <div className="font-bold text-mekomit-900 mb-1">פרטי לקוח</div>
                <div className="flex justify-between gap-2"><span className="text-ink-muted shrink-0">שם</span><span className="font-semibold text-ink-strong text-left break-words min-w-0">{customerName}</span></div>
                {customerAddress && <div className="flex justify-between gap-2"><span className="text-ink-muted shrink-0">כתובת</span><span className="text-ink-strong text-left break-words min-w-0">{customerAddress}</span></div>}
                {customerPhone && (
                    <div className="flex justify-between items-center gap-2">
                        <span className="text-ink-muted shrink-0">טלפון</span>
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-ink-strong font-mono truncate" dir="ltr">{customerPhone}</span>
                            {isMobilePhone(customerPhone) && (
                                <a href={`https://wa.me/${formatPhoneForWhatsApp(customerPhone)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center p-1.5 rounded-full bg-success-500 hover:bg-success-600 text-white transition-colors shrink-0" title="פתח שיחה בוואטסאפ">
                                    <MessageCircle className="w-3.5 h-3.5" />
                                </a>
                            )}
                        </div>
                    </div>
                )}
                {customerEmail && <div className="flex justify-between gap-2"><span className="text-ink-muted shrink-0">אימייל</span><span className="text-ink-strong text-left break-all min-w-0">{customerEmail}</span></div>}
            </div>

            {/* Payment & delivery */}
            {(order.payment_method || order.payment_status || deliveryRow || (hasTimeSlot && pickupText)) && (
                <div className="bg-white rounded-lg p-3 border border-line space-y-1">
                    <div className="font-bold text-ink-strong mb-1">תשלום ומשלוח</div>
                    {order.payment_method && <div className="flex justify-between gap-2"><span className="text-ink-muted shrink-0">תשלום</span><span className="font-medium text-ink-strong text-left break-words min-w-0">{order.payment_method}</span></div>}
                    {order.payment_status && <div className="flex justify-between gap-2"><span className="text-ink-muted shrink-0">סטטוס תשלום</span><span className={`font-semibold text-left ${order.payment_status === 'paid' ? 'text-success-600' : 'text-warning-600'}`}>{order.payment_status === 'paid' ? 'שולם' : 'ממתין לתשלום'}</span></div>}
                    {deliveryRow && <div className="flex justify-between gap-2"><span className="text-ink-muted shrink-0">{deliveryRow.label}</span><span className="font-medium text-info-700 text-left break-words min-w-0">{deliveryRow.value}</span></div>}
                    {hasTimeSlot && pickupText && (
                        <div className="flex justify-between gap-2"><span className="text-ink-muted shrink-0">זמן איסוף</span><span className="font-medium text-mekomit-700 text-left break-words min-w-0">{pickupText}</span></div>
                    )}
                </div>
            )}

            {/* Items */}
            <div>
                <div className="font-bold text-ink-strong mb-1.5">פריטים ({items.length})</div>
                <div className="space-y-1.5">
                    {items.map((item, idx) => {
                        const quantity = Number(item.quantity) || 1;
                        const lineTotal = item.total_price != null ? Number(item.total_price) : Number(item.unit_price) * quantity;
                        const unitPrice = item.unit_price != null ? Number(item.unit_price) : (quantity ? lineTotal / quantity : 0);
                        return (
                            <div key={idx} className="flex justify-between items-start gap-3 p-2 bg-white rounded-lg border border-line">
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-ink-strong break-words">{item.product_name}</div>
                                    {item.variant_name && <div className="text-[10px] text-ink-muted mt-0.5 break-words">{item.variant_name}</div>}
                                    {item.paid_options?.length > 0 && <div className="text-[10px] text-ink-subtle mt-0.5 break-words">{item.paid_options.map(o => o.name).join(', ')}</div>}
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="text-ink-muted whitespace-nowrap">{quantity > 1 ? `${quantity} × ₪${fmtMoney(unitPrice)}` : `₪${fmtMoney(unitPrice)}`}</div>
                                    <div className="font-bold text-ink-strong">₪{fmtMoney(lineTotal)}</div>
                                </div>
                            </div>
                        );
                    })}
                    {items.length === 0 && <div className="p-2 text-[11px] text-ink-subtle bg-white border border-line rounded-lg text-center">אין פריטים</div>}
                </div>
            </div>

            {/* Totals */}
            <div className="p-3 bg-white rounded-lg border border-line space-y-1">
                <div className="flex justify-between text-ink-muted"><span>סכום ביניים</span><span>₪{fmtMoney(order.subtotal || order.total)}</span></div>
                {order.discount && parseFloat(order.discount) > 0 && <div className="flex justify-between text-success-600"><span>הנחה</span><span>-₪{fmtMoney(order.discount)}</span></div>}
                {order.delivery_fee && parseFloat(order.delivery_fee) > 0 && <div className="flex justify-between text-ink-muted"><span>דמי משלוח</span><span>₪{fmtMoney(order.delivery_fee)}</span></div>}
                <div className="flex justify-between font-bold text-ink-strong pt-1.5 border-t border-line"><span>סה"כ</span><span>₪{totalPrice}</span></div>
            </div>

            {/* Notes */}
            {order.notes && (
                <div className="p-3 bg-warning-50 border border-warning-300 rounded-lg">
                    <div className="flex items-center gap-1.5 mb-1"><StickyNote className="w-3.5 h-3.5 text-warning-700 shrink-0" /><span className="font-bold text-warning-900">הערות לקוח</span></div>
                    <p className="text-warning-900 leading-relaxed whitespace-pre-wrap break-words">{order.notes}</p>
                </div>
            )}
        </div>
    );
};

// A short, gentle two-note chime so an owner notices a new order even when the
// tab is in the background. Wrapped by callers in try/catch — autoplay policies
// or missing AudioContext must never break polling.
const playChime = () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [880, 1175].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const start = now + i * 0.18;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.18);
    });
    setTimeout(() => ctx.close().catch(() => {}), 800);
};

/**
 * NewOrdersNotifier — mounted once at the app root for logged-in users.
 *
 * On load it surfaces every order still awaiting confirmation (status
 * 'pending') across ALL stores the user administers. Then, while the tab stays
 * open, it polls every 10s and surfaces any genuinely-new order too. Both land
 * in the same modal, from which the owner can jump straight to a store's
 * Orders tab. Already-confirmed orders present at load are seeded silently so
 * they never reappear.
 */
export const NewOrdersNotifier = ({ chatId }) => {
    const [stores, setStores] = useState([]);          // [{ storeId, slug, name }]
    const [newOrders, setNewOrders] = useState([]);    // [{ key, storeId, slug, storeName, order }]
    const [isOpen, setIsOpen] = useState(false);
    const [confirming, setConfirming] = useState(new Set()); // order keys currently being confirmed
    const [expanded, setExpanded] = useState(new Set());     // order keys whose details are open

    // Per-session bookkeeping. seenRef holds every order key we've already
    // accounted for; initializedStoresRef marks stores whose baseline is set.
    const seenRef = useRef(new Set());
    const initializedStoresRef = useRef(new Set());
    const storesRef = useRef([]);
    storesRef.current = stores;

    const isActive = !!chatId && chatId !== 'guest' && chatId !== 'null';

    // 1. Discover the user's stores (we need slug for navigation + name for display).
    useEffect(() => {
        if (!isActive) { setStores([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${ENTITIES_API_URL}/user-entities?chat_id=${encodeURIComponent(chatId)}`);
                const data = await res.json();
                const adminStoreIds = data?.admin_stores || [];
                if (!adminStoreIds.length) { if (!cancelled) setStores([]); return; }
                const storesData = await Promise.all(
                    adminStoreIds.map(storeId =>
                        fetch(`${STORE_API_URL}/get-store-by-id?store_id=${encodeURIComponent(storeId)}`)
                            .then(r => r.json())
                            .catch(() => null)
                    )
                );
                if (cancelled) return;
                const normalized = storesData
                    .filter(Boolean)
                    .map(s => ({ storeId: s.store_id, slug: s.store_slug, name: s.name || 'החנות שלי' }))
                    .filter(s => s.storeId && s.slug);
                setStores(normalized);
            } catch (err) {
                console.error('NewOrdersNotifier: failed to load stores', err);
            }
        })();
        return () => { cancelled = true; };
    }, [chatId, isActive]);

    // 2. Poll every store for orders we haven't seen yet.
    const poll = useCallback(async () => {
        const currentStores = storesRef.current;
        if (!currentStores.length) return;
        const discovered = [];
        await Promise.all(currentStores.map(async (store) => {
            try {
                const url = `${STORE_API_URL}/admin/store/${store.storeId}/orders?user_id=${encodeURIComponent(chatId)}`;
                const res = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
                if (!res.ok) return;
                const data = await res.json();
                const orders = data?.orders || [];
                const firstTime = !initializedStoresRef.current.has(store.storeId);
                for (const order of orders) {
                    const key = `${store.storeId}:${order.order_id}`;
                    if (seenRef.current.has(key)) continue;
                    seenRef.current.add(key);
                    // First poll (app load): surface only orders still awaiting
                    // confirmation, seed the rest silently. Later polls: every
                    // genuinely-new order is surfaced and marked as fresh.
                    if (firstTime) {
                        if (isUnconfirmed(order)) {
                            discovered.push({ key, storeId: store.storeId, slug: store.slug, storeName: store.name, order, isNew: false });
                        }
                    } else {
                        discovered.push({ key, storeId: store.storeId, slug: store.slug, storeName: store.name, order, isNew: true });
                    }
                }
                initializedStoresRef.current.add(store.storeId);
            } catch (err) {
                // Transient network error — just retry on the next tick.
            }
        }));
        if (discovered.length) {
            // Newest first within this batch, prepended above older un-handled ones.
            setNewOrders(prev => [...discovered.reverse(), ...prev]);
            setIsOpen(true);
            // Only chime for genuinely-new orders, not the on-load backlog.
            if (discovered.some(d => d.isNew)) {
                try { playChime(); } catch (_) { /* noop */ }
            }
        }
    }, [chatId]);

    useEffect(() => {
        if (!isActive || !stores.length) return;
        poll(); // immediate baseline + catch anything already pending this session
        const id = setInterval(poll, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [isActive, stores, poll]);

    // Group the pending new orders by store for a tidy, per-store layout.
    const grouped = useMemo(() => {
        const map = new Map();
        newOrders.forEach(item => {
            if (!map.has(item.storeId)) {
                map.set(item.storeId, { storeId: item.storeId, slug: item.slug, storeName: item.storeName, orders: [] });
            }
            map.get(item.storeId).orders.push(item);
        });
        return Array.from(map.values());
    }, [newOrders]);

    const dismiss = useCallback(() => { setIsOpen(false); setNewOrders([]); }, []);

    const toggleExpand = useCallback((key) => {
        setExpanded(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
    }, []);

    const goToOrders = useCallback((slug) => {
        setIsOpen(false);
        setNewOrders([]);
        navigate('store-admin', `slug=${slug}&tab=orders`);
    }, []);

    // Confirm one or more orders straight from the modal. Successfully confirmed
    // orders drop out of the list (they're no longer awaiting confirmation);
    // failures stay put so the owner can retry.
    const confirmOrders = useCallback(async (items) => {
        if (!items.length) return;
        const keys = items.map(i => i.key);
        setConfirming(prev => { const next = new Set(prev); keys.forEach(k => next.add(k)); return next; });
        const results = await Promise.all(items.map(async (item) => {
            try {
                await updateOrderStatus(item.storeId, item.order.order_id, 'confirmed', chatId);
                return { key: item.key, ok: true };
            } catch (err) {
                return { key: item.key, ok: false };
            }
        }));
        const okKeys = new Set(results.filter(r => r.ok).map(r => r.key));
        setNewOrders(prev => prev.filter(o => !okKeys.has(o.key)));
        setConfirming(prev => { const next = new Set(prev); keys.forEach(k => next.delete(k)); return next; });
        const failed = results.length - okKeys.size;
        if (failed > 0) alert(failed === 1 ? 'שגיאה באישור ההזמנה' : `שגיאה באישור ${failed} הזמנות`);
    }, [chatId]);

    if (!isOpen || newOrders.length === 0) return null;

    const totalCount = newOrders.length;

    return (
        <div
            className="fixed inset-0 z-[12000] flex items-end sm:items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm"
            onClick={dismiss}
            dir="rtl"
        >
            <div
                className="bg-white rounded-2xl w-full max-w-lg shadow-elevated animate-scale-in max-h-[85vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between gap-3 p-4 sm:p-5 border-b border-line-subtle bg-gradient-to-l from-mekomit-50 to-white">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="relative shrink-0">
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-mekomit-500 to-mekomit-400 flex items-center justify-center shadow-sm">
                                <BellRing className="w-5 h-5 text-white" />
                            </div>
                            <span className="absolute -top-1 -left-1 min-w-[20px] h-5 px-1 flex items-center justify-center bg-danger-600 text-white text-[11px] font-bold rounded-full shadow tabular-nums">{totalCount}</span>
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-base sm:text-lg font-bold text-mekomit-900 truncate">הזמנות לאישור</h3>
                            <p className="text-[11px] sm:text-xs text-ink-muted">{totalCount === 1 ? 'הזמנה אחת ממתינה לאישור' : `${totalCount} הזמנות ממתינות לאישור`}</p>
                        </div>
                    </div>
                    <button type="button" onClick={dismiss} className="p-2 rounded-lg hover:bg-surface-muted text-ink-muted shrink-0" aria-label="סגור">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body — grouped by store */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
                    {grouped.map(group => (
                        <div key={group.storeId} className="border border-mekomit-100 rounded-xl overflow-hidden">
                            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-mekomit-50 border-b border-mekomit-100">
                                <div className="flex items-center gap-2 min-w-0">
                                    <ShoppingCart className="w-4 h-4 text-mekomit-600 shrink-0" />
                                    <span className="text-sm font-bold text-mekomit-900 truncate" title={group.storeName}>{group.storeName}</span>
                                    <span className="text-[10px] font-bold text-mekomit-700 bg-white border border-mekomit-200 rounded-full px-1.5 py-0.5 tabular-nums shrink-0">{group.orders.length}</span>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {group.orders.length > 1 && (() => {
                                        const groupBusy = group.orders.every(o => confirming.has(o.key));
                                        return (
                                            <button
                                                type="button"
                                                onClick={() => confirmOrders(group.orders)}
                                                disabled={groupBusy}
                                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-success-600 hover:bg-success-700 disabled:opacity-60 text-white text-[11px] font-semibold shadow-sm transition-colors"
                                            >
                                                <Check className="w-3.5 h-3.5" />
                                                {groupBusy ? 'מאשר…' : 'אשר הכול'}
                                            </button>
                                        );
                                    })()}
                                    <button
                                        type="button"
                                        onClick={() => goToOrders(group.slug)}
                                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-mekomit-500 hover:bg-mekomit-600 text-white text-[11px] font-semibold shadow-sm transition-colors"
                                    >
                                        לניהול
                                        <ArrowLeft className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                            <div className="divide-y divide-line-subtle">
                                {group.orders.map((item) => {
                                    const { key, slug, order, isNew } = item;
                                    const customerName = order.customer?.name || order.customer_name || 'לקוח';
                                    const total = order.total || order.total_price || '0';
                                    const itemCount = order.items?.length || 0;
                                    const created = order.created_at ? new Date(order.created_at) : null;
                                    const busy = confirming.has(key);
                                    const isExpanded = expanded.has(key);
                                    return (
                                        <div key={key}>
                                            <div className="w-full flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-mekomit-50/60 transition-colors">
                                                <button type="button" onClick={() => toggleExpand(key)} className="flex-1 min-w-0 text-right flex items-center gap-2" aria-expanded={isExpanded}>
                                                    <ChevronDown className={`w-4 h-4 text-ink-subtle shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                    <span className="min-w-0">
                                                        <span className="flex items-center gap-1.5">
                                                            {isNew && <span className="shrink-0 text-[9px] font-bold text-white bg-danger-600 rounded-full px-1.5 py-0.5">חדש</span>}
                                                            <span className="text-sm font-semibold text-ink-strong truncate" title={customerName}>{customerName}</span>
                                                        </span>
                                                        <span className="text-[11px] text-ink-subtle flex items-center gap-1.5 flex-wrap">
                                                            <span>{itemCount} פריטים</span>
                                                            {created && (<><span>•</span><span>{created.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}</span></>)}
                                                        </span>
                                                    </span>
                                                </button>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <div className="text-base font-bold text-mekomit-600 tabular-nums whitespace-nowrap">₪{total}</div>
                                                    <button
                                                        type="button"
                                                        onClick={() => confirmOrders([item])}
                                                        disabled={busy}
                                                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-success-600 hover:bg-success-700 disabled:opacity-60 text-white text-[11px] font-semibold shadow-sm transition-colors"
                                                        title="אשר הזמנה"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                        {busy ? '…' : 'אשר'}
                                                    </button>
                                                </div>
                                            </div>
                                            {isExpanded && (
                                                <>
                                                    <OrderDetails order={order} />
                                                    <div className="px-3 pb-3 bg-surface-muted/50">
                                                        <button type="button" onClick={() => goToOrders(slug)} className="w-full flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-mekomit-500 hover:bg-mekomit-600 text-white text-[11px] font-semibold shadow-sm transition-colors">
                                                            פתח בלשונית ההזמנות
                                                            <ArrowLeft className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="p-3 sm:p-4 border-t border-line-subtle bg-white">
                    <button
                        type="button"
                        onClick={dismiss}
                        className="w-full px-4 py-2 rounded-lg border border-line-strong text-ink-strong text-sm font-semibold hover:bg-surface-muted transition-colors"
                    >
                        סגור
                    </button>
                </div>
            </div>
        </div>
    );
};
